import React, { useState } from "react";
import { FMEPageHeader, useModelContext, ScenarioSensitivitySelector } from '../../components/Layout';
import { useDashboardSummary } from '../../shared/hooks/queries/useDashboard';
import { useComputeStore } from '../../shared/stores/useComputeStore';

// Dashboard components
import DashboardTabs from "../../components/Dashboard/DashboardTabs";
import CompanySelector from "../../components/Dashboard/CompanySelector";
import {
  OverviewTab,
  ProfitLossTab,
  BalanceSheetTab,
  CashFlowTab,
  KPIsTab,
  OperationalTab,
  SensitivitiesTab,
} from "../../components/Dashboard/tabs";

// Tab configuration
const DASHBOARD_TABS = [
  { id: 'overview', label: 'Overview', icon: 'dashboard' },
  { id: 'pnl', label: 'P&L', icon: 'trending_up' },
  { id: 'balance', label: 'Balance Sheet', icon: 'account_balance' },
  { id: 'cashflow', label: 'Cash Flow', icon: 'payments' },
  { id: 'operational', label: 'Operational', icon: 'settings' },
  { id: 'kpis', label: 'KPIs', icon: 'monitoring' },
  { id: 'sensitivities', label: 'Sensitivities', icon: 'tune' },
];

export default function Dashboard() {
  const { model, currentScenario, currentSensitivity, currentEntity, computeGeneration, isComputing, contextFirstError, contextSensitivityErrorCount } = useModelContext();
  const computeError = useComputeStore((s) => s.computeError);

  if (!currentEntity || !currentScenario || !model) {
    return (
        <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-slate-500 font-medium">Loading...</span>
            </div>
        </div>
    );
  }

  return <DashboardContent currentEntity={currentEntity} model={model} currentScenario={currentScenario} currentSensitivity={currentSensitivity} computeGeneration={computeGeneration} isComputing={isComputing} computeError={computeError} contextFirstError={contextFirstError} contextSensitivityErrorCount={contextSensitivityErrorCount} />;
}

// Utility function for generating random strings (used by other components)
export function generateString(length: number): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

// Content component: business logic and rendering
function DashboardContent({ currentEntity, model, currentScenario, currentSensitivity, computeGeneration, isComputing, computeError, contextFirstError, contextSensitivityErrorCount }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState(0);

  // Dashboard is available only if: no errors AND dashboard_computed is true
  const entityForDashboard = currentSensitivity || currentScenario;
  const hasErrors = computeError || contextFirstError || (currentSensitivity && contextSensitivityErrorCount > 0);
  const dashboardReady = entityForDashboard?.dashboard_computed && !hasErrors;

  const { data: summary = {}, isLoading: loading } = useDashboardSummary(
    currentEntity.id,
    currentEntity.type,
    currentEntity.type === 'sensitivity' ? currentEntity.scenario_id : undefined,
    computeGeneration,
    !!dashboardReady,
  );

  // Get current company data
  const companies = summary.company_dashboard || [];
  const currentCompany = companies[selectedCompanyIndex] || null;

  // Filter tabs based on entity type
  const visibleTabs = currentEntity.type === 'sensitivity'
    ? DASHBOARD_TABS.filter(tab => tab.id !== 'sensitivities')
    : DASHBOARD_TABS;

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab summary={summary} />;
      case 'pnl':
        return <ProfitLossTab companyData={currentCompany} />;
      case 'balance':
        return <BalanceSheetTab companyData={currentCompany} />;
      case 'cashflow':
        return <CashFlowTab companyData={currentCompany} />;
      case 'operational':
        return <OperationalTab companyData={currentCompany} />;
      case 'kpis':
        return <KPIsTab companyData={currentCompany} />;
      case 'sensitivities':
        return <SensitivitiesTab companyData={currentCompany} entityType={currentEntity.type} />;
      default:
        return <OverviewTab summary={summary} />;
    }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <FMEPageHeader
        title="Dashboard"
        breadcrumb={[model.name]}
        description="Visualize key metrics, KPIs, and performance charts for your financial model."
        actions={<ScenarioSensitivitySelector />}
      />

      <div className="px-8 py-6 min-w-0">
        {isComputing ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-slate-500">Calculating model...</p>
            </div>
          </div>
        ) : hasErrors ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="size-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <span className="material-symbols-outlined text-[32px] text-red-400">error</span>
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">The Dashboard is not available</h3>
            <p className="text-slate-500 max-w-md">
              Please complete all required inputs in the Architecture section.
            </p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-slate-500">Loading dashboard...</p>
            </div>
          </div>
        ) : dashboardReady && summary.project_name ? (
          <div className="space-y-6 min-w-0">
            {/* Tabs Navigation */}
            <DashboardTabs
              tabs={visibleTabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />

            {/* Company Selector (for company-specific tabs) */}
            {activeTab !== 'overview' && companies.length > 0 && (
              <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-3">
                <CompanySelector
                  companies={companies}
                  selectedIndex={selectedCompanyIndex}
                  onSelect={setSelectedCompanyIndex}
                />
              </div>
            )}

            {/* Tab Content */}
            <div className="mt-6 min-w-0 overflow-hidden">
              {renderTabContent()}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="size-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <span className="material-symbols-outlined text-[32px] text-slate-400">hourglass_empty</span>
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">The Dashboard is not computed</h3>
            <p className="text-slate-500 max-w-md">
              Please design your architecture and complete all required inputs to see the computed results.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
